// Client-side WebAuthn helpers for medical vault unlock + passkey enrollment.
//
// Mirrors the legacy `@simplewebauthn/browser` flow but uses native browser
// APIs verbatim, so apps/app doesn't pull in another dependency. The wire
// shapes (base64url challenges / IDs) match the @simplewebauthn/server output
// served by apps/api at /api/account/vault-security/biometric/*.

import { apiFetch, ApiError } from "@pontimed/api-client";
import { adoptNativeSession } from "@pontimed/auth-client";

// --- base64url helpers -------------------------------------------------------

function base64urlToBytes(b64u: string): ArrayBuffer {
  const pad = b64u.length % 4 === 0 ? "" : "=".repeat(4 - (b64u.length % 4));
  const b64 = (b64u + pad).replace(/-/g, "+").replace(/_/g, "/");
  const bin = atob(b64);
  // Return a fresh ArrayBuffer (not SharedArrayBuffer) so this works as a
  // BufferSource everywhere TS expects it (e.g. WebAuthn challenge fields).
  const buf = new ArrayBuffer(bin.length);
  const bytes = new Uint8Array(buf);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return buf;
}

function bytesToBase64url(bytes: ArrayBuffer | Uint8Array): string {
  const buf = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  let bin = "";
  for (let i = 0; i < buf.length; i++) bin += String.fromCharCode(buf[i]);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

// --- Wire JSON shapes (mirrors @simplewebauthn/server output) ----------------

interface PublicKeyCredentialDescriptorJSON {
  id: string;
  type: "public-key";
  transports?: AuthenticatorTransport[];
}

interface PublicKeyCredentialUserEntityJSON {
  id: string;
  name: string;
  displayName: string;
}

interface PublicKeyCredentialRpEntityJSON {
  id?: string;
  name: string;
}

interface PublicKeyCredentialParameters {
  type: "public-key";
  alg: number;
}

interface AuthenticatorSelectionCriteria {
  authenticatorAttachment?: AuthenticatorAttachment;
  requireResidentKey?: boolean;
  residentKey?: ResidentKeyRequirement;
  userVerification?: UserVerificationRequirement;
}

interface PublicKeyCredentialCreationOptionsJSON {
  rp: PublicKeyCredentialRpEntityJSON;
  user: PublicKeyCredentialUserEntityJSON;
  challenge: string;
  pubKeyCredParams: PublicKeyCredentialParameters[];
  timeout?: number;
  excludeCredentials?: PublicKeyCredentialDescriptorJSON[];
  authenticatorSelection?: AuthenticatorSelectionCriteria;
  attestation?: AttestationConveyancePreference;
  extensions?: Record<string, unknown>;
}

interface PublicKeyCredentialRequestOptionsJSON {
  challenge: string;
  timeout?: number;
  rpId?: string;
  allowCredentials?: PublicKeyCredentialDescriptorJSON[];
  userVerification?: UserVerificationRequirement;
  extensions?: Record<string, unknown>;
}

// Encoded credential payload sent back to the server. Matches the format
// @simplewebauthn/server's `verifyRegistrationResponse` / `verifyAuthenticationResponse`
// expects (see RegistrationResponseJSON / AuthenticationResponseJSON).

interface RegistrationResponseJSON {
  id: string;
  rawId: string;
  type: "public-key";
  response: {
    clientDataJSON: string;
    attestationObject: string;
    transports?: AuthenticatorTransport[];
  };
  clientExtensionResults: AuthenticationExtensionsClientOutputs;
  authenticatorAttachment?: AuthenticatorAttachment;
}

interface AuthenticationResponseJSON {
  id: string;
  rawId: string;
  type: "public-key";
  response: {
    clientDataJSON: string;
    authenticatorData: string;
    signature: string;
    userHandle?: string;
  };
  clientExtensionResults: AuthenticationExtensionsClientOutputs;
  authenticatorAttachment?: AuthenticatorAttachment;
}

// --- Public API --------------------------------------------------------------

/**
 * Triggers the biometric unlock flow against /api/account/vault-security/biometric/*.
 * Returns true on successful verification, false if the user cancels the prompt.
 * Throws on network / server errors.
 */
export async function unlockWithBiometric(): Promise<boolean> {
  const optionsJSON = await apiFetch.post<PublicKeyCredentialRequestOptionsJSON>(
    "/api/account/vault-security/biometric/auth/options",
  );

  let assertion: AuthenticationResponseJSON | null;
  try {
    assertion = await getAssertion(optionsJSON);
  } catch (err) {
    if (err instanceof DOMException && err.name === "NotAllowedError") {
      // User cancelled the OS prompt — surfaced as cancellation, not error.
      return false;
    }
    throw err;
  }
  if (!assertion) return false;

  try {
    await apiFetch.post("/api/account/vault-security/biometric/auth/verify", {
      response: assertion,
    });
  } catch (err) {
    if (err instanceof ApiError) throw new Error("verify_failed");
    throw err;
  }
  return true;
}

/** A new passkey made on this device, not yet registered (the server holds its challenge). */
export type PasskeyAttestation = RegistrationResponseJSON;

/**
 * Step 1 of a registration: asks the server for options and the OS for a new
 * passkey. Null when the person cancels the OS prompt. The server keeps the
 * challenge until a registration is accepted, so a refusal of step 2 — the
 * vault locked, a wrong PIN or code — can be answered and submitted again
 * with the same attestation.
 */
export async function createPasskeyAttestation(): Promise<PasskeyAttestation | null> {
  const optionsJSON = await apiFetch.post<PublicKeyCredentialCreationOptionsJSON>(
    "/api/account/vault-security/biometric/register/options",
  );
  try {
    return await createCredential(optionsJSON);
  } catch (err) {
    if (err instanceof DOMException && err.name === "NotAllowedError") return null;
    throw err;
  }
}

export interface PasskeyRegistrationOptions {
  deviceLabel?: string;
  /** "sign_in": the post-login offer — passwordless sign-in (`account_login`). */
  intent?: "sign_in";
  /**
   * The vault's current PIN: on a PIN-protected vault, a passkey unlocks the
   * vault only if its owner proved the PIN now (or holds a live unlock).
   */
  currentPin?: string;
  /** A code typed now (authenticator or recovery): the passkey also confirms two-step verification. */
  twoFactorCode?: string;
}

/**
 * Step 2: registers the attestation. Throws ApiError on a refusal —
 * `vault_locked` (prove the PIN), `current_pin_invalid`,
 * `invalid_second_factor` / `code_already_used`, 403 TOTP_CHALLENGE_REQUIRED
 * (the MFA enforcer's cue), 429.
 */
export async function submitPasskeyRegistration(
  attestation: PasskeyAttestation,
  options: PasskeyRegistrationOptions = {},
): Promise<{ stepUp2fa: boolean; vaultUnlock: boolean }> {
  const result = await apiFetch.post<{ stepUp2fa?: boolean; vaultUnlock?: boolean }>(
    "/api/account/vault-security/biometric/register/verify",
    {
      response: attestation,
      deviceLabel: options.deviceLabel,
      ...(options.intent ? { intent: options.intent } : {}),
      ...(options.currentPin ? { currentPin: options.currentPin } : {}),
      ...(options.twoFactorCode ? { twoFactorCode: options.twoFactorCode } : {}),
    },
  );
  if (options.intent === "sign_in") markPasskeyLoginAvailable();
  return { stepUp2fa: result?.stepUp2fa === true, vaultUnlock: result?.vaultUnlock === true };
}

/**
 * Enrolls a new passkey via /api/account/vault-security/biometric/register/*.
 * Returns true on success, false if user cancels.
 *
 * `intent: "sign_in"` marks the credential usable for passwordless first-factor
 * login (`account_login`). Without it the FIRST passkey a user ever enrols gets
 * `vault_unlock` only, so "Sign in with biometrics" 404s until they find the
 * separate Sign-in toggle in Medical Vault → Protected areas. With the vault
 * locked, a sign-in enrolment becomes a sign-in passkey only (the vault keeps
 * asking for its PIN); a vault enrolment needs the PIN — use the two steps
 * above to answer that.
 */
export async function enrollPasskey(deviceLabel?: string, intent?: "sign_in"): Promise<boolean> {
  const attestation = await createPasskeyAttestation();
  if (!attestation) return false;
  await submitPasskeyRegistration(attestation, { deviceLabel, intent });
  return true;
}

/**
 * Makes an existing passkey also confirm two-step verification (the
 * `step_up_2fa` marker), via
 * /api/account/vault-security/biometric/credentials/:id/promote/*: a WebAuthn
 * assertion by THAT credential (the server allows no other) and a second
 * factor typed now (`twoFactorCode`: an authenticator or recovery code — the
 * trusted-device check does not count). `credentialRowId` is the `id` the
 * passkey list returns. Returns true on success, false if the user cancels the
 * OS prompt. Throws ApiError on a refusal (`invalid_second_factor`,
 * `two_factor_not_mature`, 429…).
 */
export async function promotePasskeyToTwoFactor(
  credentialRowId: string,
  twoFactorCode: string,
): Promise<boolean> {
  const base = `/api/account/vault-security/biometric/credentials/${encodeURIComponent(
    credentialRowId,
  )}/promote`;
  const optionsJSON = await apiFetch.post<PublicKeyCredentialRequestOptionsJSON>(`${base}/options`);

  let assertion: AuthenticationResponseJSON | null;
  try {
    assertion = await getAssertion(optionsJSON);
  } catch (err) {
    if (err instanceof DOMException && err.name === "NotAllowedError") return false;
    throw err;
  }
  if (!assertion) return false;

  await apiFetch.post(`${base}/verify`, { response: assertion, twoFactorCode });
  return true;
}

// --- "has this device ever signed in with a passkey?" -----------------------
//
// WebAuthn deliberately reports "user cancelled" and "no credential for this
// RP" as the SAME `NotAllowedError` — the spec hides the difference so a site
// cannot probe which passkeys you hold. That left the modal unable to tell a
// first-time visitor (nothing enrolled) from someone who dismissed the OS
// sheet, so it treated both as "cancel" and did nothing at all: the biometric
// button looked broken.
//
// This local marker breaks the tie without leaking anything: set it once a
// passkey login (or a sign-in enrollment) has actually succeeded here. If it
// is absent and the OS call comes back empty-handed, "not set up yet" is the
// overwhelmingly likely explanation and the guidance card is the right answer.
const PASSKEY_LOGIN_MARKER = "pontimed_passkey_login_ok";

export function markPasskeyLoginAvailable(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(PASSKEY_LOGIN_MARKER, "1");
  } catch {
    /* private mode — fall back to showing the guidance card */
  }
}

export function hasPasskeyLoginOnThisDevice(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(PASSKEY_LOGIN_MARKER) === "1";
  } catch {
    return false;
  }
}

/**
 * Anonymous primary sign-in. Triggers the OS passkey picker; on success the
 * server mints fresh access + refresh cookies. Returns true on success, false
 * if user cancels. Throws on network / verification errors.
 */
export async function signInWithPasskey(rememberMe = true): Promise<boolean> {
  const optionsJSON = await apiFetch.post<
    PublicKeyCredentialRequestOptionsJSON & { sessionId: string }
  >("/api/auth/passkey/login/options");

  let assertion: AuthenticationResponseJSON | null;
  try {
    assertion = await getAssertion(optionsJSON);
  } catch (err) {
    if (err instanceof DOMException && err.name === "NotAllowedError") return false;
    throw err;
  }
  if (!assertion) return false;

  // The native app keeps the session it is answered with (a no-op on the web).
  await adoptNativeSession(
    await apiFetch.post<unknown>("/api/auth/passkey/login/verify", {
      sessionId: optionsJSON.sessionId,
      response: assertion,
      rememberMe,
    }),
  );
  markPasskeyLoginAvailable();
  return true;
}

/**
 * Detect whether the current device can offer passkey sign-in. Returns true
 * if the platform exposes a user-verifying authenticator (Face ID / Touch ID
 * / Windows Hello / Android biometric). Resolves to false on unsupported
 * browsers or if the platform authenticator is unavailable.
 */
export async function isPasskeySupported(): Promise<boolean> {
  if (typeof window === "undefined" || !window.PublicKeyCredential) return false;
  try {
    return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    return false;
  }
}

// --- Native API wrappers -----------------------------------------------------

async function getAssertion(
  optionsJSON: PublicKeyCredentialRequestOptionsJSON,
): Promise<AuthenticationResponseJSON | null> {
  if (typeof window === "undefined" || !window.PublicKeyCredential) {
    throw new Error("WebAuthn not supported");
  }

  const publicKey: PublicKeyCredentialRequestOptions = {
    challenge: base64urlToBytes(optionsJSON.challenge),
    timeout: optionsJSON.timeout,
    rpId: optionsJSON.rpId,
    userVerification: optionsJSON.userVerification,
    allowCredentials: optionsJSON.allowCredentials?.map((c) => ({
      id: base64urlToBytes(c.id),
      type: c.type,
      transports: c.transports,
    })),
  };

  const credential = (await navigator.credentials.get({ publicKey })) as PublicKeyCredential | null;
  if (!credential) return null;

  const response = credential.response as AuthenticatorAssertionResponse;

  return {
    id: credential.id,
    rawId: bytesToBase64url(credential.rawId),
    type: "public-key",
    response: {
      clientDataJSON: bytesToBase64url(response.clientDataJSON),
      authenticatorData: bytesToBase64url(response.authenticatorData),
      signature: bytesToBase64url(response.signature),
      userHandle: response.userHandle ? bytesToBase64url(response.userHandle) : undefined,
    },
    clientExtensionResults: credential.getClientExtensionResults(),
    authenticatorAttachment:
      (credential.authenticatorAttachment as AuthenticatorAttachment | null) ?? undefined,
  };
}

async function createCredential(
  optionsJSON: PublicKeyCredentialCreationOptionsJSON,
): Promise<RegistrationResponseJSON | null> {
  if (typeof window === "undefined" || !window.PublicKeyCredential) {
    throw new Error("WebAuthn not supported");
  }

  const publicKey: PublicKeyCredentialCreationOptions = {
    challenge: base64urlToBytes(optionsJSON.challenge),
    rp: optionsJSON.rp,
    user: {
      id: base64urlToBytes(optionsJSON.user.id),
      name: optionsJSON.user.name,
      displayName: optionsJSON.user.displayName,
    },
    pubKeyCredParams: optionsJSON.pubKeyCredParams,
    timeout: optionsJSON.timeout,
    attestation: optionsJSON.attestation,
    authenticatorSelection: optionsJSON.authenticatorSelection,
    excludeCredentials: optionsJSON.excludeCredentials?.map((c) => ({
      id: base64urlToBytes(c.id),
      type: c.type,
      transports: c.transports,
    })),
  };

  const credential = (await navigator.credentials.create({
    publicKey,
  })) as PublicKeyCredential | null;
  if (!credential) return null;

  const response = credential.response as AuthenticatorAttestationResponse;
  const transports =
    typeof response.getTransports === "function" ? response.getTransports() : undefined;

  return {
    id: credential.id,
    rawId: bytesToBase64url(credential.rawId),
    type: "public-key",
    response: {
      clientDataJSON: bytesToBase64url(response.clientDataJSON),
      attestationObject: bytesToBase64url(response.attestationObject),
      transports: transports as AuthenticatorTransport[] | undefined,
    },
    clientExtensionResults: credential.getClientExtensionResults(),
    authenticatorAttachment:
      (credential.authenticatorAttachment as AuthenticatorAttachment | null) ?? undefined,
  };
}
